import React, { useState, useEffect, useCallback } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

import JobsTable from './JobsTable';
import JobFilters from './JobFilters';
import CreateScheduleModal from './CreateScheduleModal';
import EditScheduleModal from './EditScheduleModal';
import JobStatusCards from './JobStatusCards';
import SyncOptionsDialog from '../connectors/SyncOptionsDialog';
import BrandPipelinesSection from './BrandPipelinesSection';
import AccountingPipelinesSection from '../finance/AccountingPipelinesSection';
import GoogleSheetsSchedulesSection from './GoogleSheetsSchedulesSection';

export default function ScheduledJobsPage() {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const location = useLocation();
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [recentJobs, setRecentJobs] = useState([]);
  
  // Filter state
  const [filters, setFilters] = useState({
    brand_id: '',
    platform_id: '',
    status: 'all',
    job_status: 'all',
    page: 1,
    limit: 20,
    sort_by: 'status',
    sort_dir: 'asc'
  });
  
  // Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [preSelectedConnectorId, setPreSelectedConnectorId] = useState(null);
  
  // Sync modal state
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [selectedConnectorForSync, setSelectedConnectorForSync] = useState(null);
  
  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Pagination state
  const [pagination, setPagination] = useState({});

  // Load schedules with optional loading state
  const loadSchedules = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'all') {
          params.append(key, filters[key]);
        }
      });
      
      const response = await axios.get(`/api/connectors/schedules?${params}`);
      
      console.log('=== SCHEDULES API RESPONSE DEBUG ===');
      console.log('Full response:', response.data);
      console.log('First schedule object:', response.data.schedules?.[0]);
      if (response.data.schedules?.[0]) {
        console.log('First schedule keys:', Object.keys(response.data.schedules[0]));
        console.log('Connector ID from first schedule:', response.data.schedules[0].connector_id);
      }
      
      setSchedules(response.data.schedules || []);
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Error loading schedules:', error);
      if (showLoading) {
        showNotification('Failed to load scheduled jobs', 'error');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [filters]);

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      const statsResponse = await axios.get('/api/connectors/schedules/stats');
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  // Handle URL parameters for opening modal with pre-selected connector
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const createSchedule = urlParams.get('create');
    const connectorId = urlParams.get('connector_id');
    
    if (createSchedule === 'true') {
      setCreateModalOpen(true);
      if (connectorId) {
        setPreSelectedConnectorId(connectorId);
      }
      // Clear URL parameters after opening modal
      navigate('/scheduled-jobs', { replace: true });
    }
  }, [location.search, navigate]);

  // Initial load and auto-refresh
  useEffect(() => {
    loadSchedules(); // Initial load with loading spinner
    loadStats();
    
    // Auto-refresh every 30 seconds (silent, no loading spinner)
    const interval = setInterval(() => {
      loadSchedules(false); // false = no loading spinner for auto-refresh
      loadStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [loadSchedules, loadStats]);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newLimit) => {
    setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // Handle sort change from JobsTable
  const handleSortChange = (sortBy, sortDir) => {
    setFilters(prev => ({ ...prev, sort_by: sortBy, sort_dir: sortDir, page: 1 }));
  };

  // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  // Handle actions
  const handleToggleSchedule = async (schedule) => {
    try {
      await axios.post(`/api/connectors/schedules/${schedule.schedule_id}/toggle`);
      showNotification(
        `Schedule ${schedule.is_enabled ? 'disabled' : 'enabled'} successfully`,
        'success'
      );
      loadSchedules();
    } catch (error) {
      console.error('Error toggling schedule:', error);
      showNotification('Failed to toggle schedule status', 'error');
    }
  };

  const handleTriggerSync = (schedule) => {
    console.log('=== FRONTEND TRIGGER SYNC DEBUG ===');
    console.log('Schedule object:', schedule);
    console.log('Connector ID from schedule.connector_id:', schedule.connector_id);
    console.log('Connector ID from schedule["s.connector_id"]:', schedule['s.connector_id']);
    
    // Get connector ID from either property name
    const connectorId = schedule.connector_id || schedule['s.connector_id'];
    
    console.log('Final connector ID used:', connectorId);
    
    // Convert schedule to connector format expected by SyncOptionsDialog
    const connectorForSync = {
      id: connectorId,
      name: schedule.connector_name,
      platform_name: schedule.platform_name,
      sync_window_days: schedule.sync_window_days
    };
    
    console.log('Connector object for sync:', connectorForSync);
    
    setSelectedConnectorForSync(connectorForSync);
    setSyncDialogOpen(true);
  };
  
  const handleSyncQueued = (result) => {
    if (result.jobId) {
      showNotification(`Sync queued successfully! Position: ${result.queuePosition || 'N/A'}`, 'success');
      loadStats(); // Refresh recent jobs
    } else if (result.error) {
      showNotification(`Sync failed: ${result.error}`, 'error');
    }
  };

  const handleDeleteSchedule = async (schedule) => {
    if (!await confirm({ title: 'Delete Schedule', message: 'Are you sure you want to delete this schedule?', confirmText: 'Delete', severity: 'danger' })) {
      return;
    }
    
    try {
      await axios.delete(`/api/connectors/schedules/${schedule.schedule_id}`);
      showNotification('Schedule deleted successfully', 'success');
      loadSchedules();
      loadStats();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      showNotification('Failed to delete schedule', 'error');
    }
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setEditModalOpen(true);
  };

  const handleModalSuccess = () => {
    setCreateModalOpen(false);
    loadSchedules();
    loadStats();
  };

  const handleEditModalSuccess = () => {
    setEditModalOpen(false);
    setSelectedSchedule(null);
    showNotification('Schedule updated successfully', 'success');
    loadSchedules();
    loadStats();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="600">
          Scheduled Jobs
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadSchedules(true)}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
          >
            Add Schedule
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <JobStatusCards stats={stats} sx={{ mb: 3 }} />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <JobFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          loading={loading}
        />
      </Paper>

      {/* Connector Schedules Table */}
      <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
        Connector Sync Schedules
      </Typography>
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <JobsTable
            schedules={schedules}
            pagination={pagination}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onToggleSchedule={handleToggleSchedule}
            onTriggerSync={handleTriggerSync}
            onDeleteSchedule={handleDeleteSchedule}
            onEditSchedule={handleEditSchedule}
            sortBy={filters.sort_by}
            sortDir={filters.sort_dir}
            onSortChange={handleSortChange}
          />
        )}
      </Paper>

      {/* Brand Pipelines Section */}
      <BrandPipelinesSection onNotification={showNotification} />

      {/* Accounting Pipelines Section */}
      <AccountingPipelinesSection onNotification={showNotification} />

      {/* Google Sheets Schedules Section */}
      <GoogleSheetsSchedulesSection onNotification={showNotification} />

      {/* Create Schedule Modal */}
      <CreateScheduleModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setPreSelectedConnectorId(null);
        }}
        onSuccess={handleModalSuccess}
        onError={(message) => showNotification(message, 'error')}
        preSelectedConnectorId={preSelectedConnectorId}
      />

      {/* Edit Schedule Modal */}
      <EditScheduleModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedSchedule(null);
        }}
        onSuccess={handleEditModalSuccess}
        onError={(message) => showNotification(message, 'error')}
        schedule={selectedSchedule}
      />
      
      {/* Sync Options Dialog */}
      <SyncOptionsDialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        connector={selectedConnectorForSync}
        onSyncQueued={handleSyncQueued}
      />

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}
